import React, { useState, useRef, useEffect } from 'react';

import Select from 'react-select';
import { BUSINESSHOURS } from '../../../utils';
import { Clock } from 'lucide-react';

const customMultiSelectorStyle = {
  control: (provided) => ({
    ...provided,
    width: '88px',
    cursor: 'pointer'
  }),

  indicatorSeparator: () => ({
    display: 'none'
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: '#3B5998',
    color: 'black',
    borderRadius: '0px'
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: '205px'
  }),
  valueContainer: (provided) => ({
    ...provided,
    maxHeight: '35px',
    overflowY: 'auto',
    overflowX: 'hidden',
    scrollbarWidth: 'thin',
    scrollbarColor: 'white white',
    '&::-webkit-scrollbar': {
      width: '8px'
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'white',
      borderRadius: '8px'
    }
  })
};
const DayRow = ({
  dayName,
  value,
  setFieldValue,
  start,
  end,
  setStart,
  setEnd,
  isActive,
  setActive
}) => (
  <div className="flex items-center gap-3 py-1">
    <button
      type="button"
      aria-pressed={isActive}
      onClick={() => setActive((prev) => !prev)}
      className={`w-10 h-6 rounded-full border-2 flex items-center transition-colors duration-200 focus:outline-none ${
        isActive ? 'bg-primary border-primary' : 'bg-muted border-border'
      }`}>
      <span
        className={`inline-block w-5 h-5 rounded-full bg-white shadow transform transition-transform duration-200 ${
          isActive ? 'translate-x-4' : 'translate-x-0'
        }`}></span>
    </button>
    <span className="w-10 text-sm font-medium text-foreground">
      {dayName.slice(0, 3)}
    </span>
    {isActive ? (
      <>
        <Select
          required
          options={BUSINESSHOURS}
          className="basic-single min-w-[90px]"
          isSearchable={false}
          value={BUSINESSHOURS.find((bh) => bh.value === Number(start))}
          onChange={(selectedOption) => {
            setStart(selectedOption.value);
            setFieldValue(dayName, `${selectedOption.value}/${end}`);
          }}
          styles={customMultiSelectorStyle}
        />
        <span className="mx-1 text-base text-muted-foreground">to</span>
        <Select
          required
          options={BUSINESSHOURS}
          className="basic-single min-w-[90px]"
          isSearchable={false}
          value={BUSINESSHOURS.find((bh) => bh.value === Number(end))}
          onChange={(selectedOption) => {
            setEnd(selectedOption.value);
            setFieldValue(dayName, `${start}/${selectedOption.value}`);
          }}
          styles={customMultiSelectorStyle}
        />
      </>
    ) : (
      <span className="text-muted-foreground text-sm ml-2">Closed</span>
    )}
  </div>
);

const BusinessHours = ({ values, setFieldValue }) => {
  const days = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday'
  ];
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const ref = useRef();
  // State for each day
  const [active, setActive] = useState(() =>
    days.map((day) => values.businessHours[day.toLowerCase()] !== '/')
  );
  const [starts, setStarts] = useState(() =>
    days.map(
      (day) => values.businessHours[day.toLowerCase()].split('/')[0] || '9'
    )
  );
  const [ends, setEnds] = useState(() =>
    days.map(
      (day) => values.businessHours[day.toLowerCase()].split('/')[1] || '18'
    )
  );

  // Click outside to close
  useEffect(() => {
    function handleClick(e) {
      if (dropdownOpen && ref.current && !ref.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [dropdownOpen]);

  // Summary string
  function getSummary() {
    const openDays = days.filter((_, i) => active[i]);
    if (openDays.length === 0) return 'Closed';
    if (
      openDays.length === 7 &&
      starts.every((v, i) => v === starts[0] && ends[i] === ends[0])
    ) {
      return `Everyday ${formatHour(starts[0])} - ${formatHour(ends[0])}`;
    }
    // Group consecutive days with same hours
    let summary = '';
    let i = 0;
    while (i < 7) {
      if (!active[i]) {
        i++;
        continue;
      }
      let j = i;
      while (
        j + 1 < 7 &&
        active[j + 1] &&
        starts[j + 1] === starts[i] &&
        ends[j + 1] === ends[i]
      )
        j++;
      summary += `${days[i].slice(0, 3)}${
        j > i ? '-' + days[j].slice(0, 3) : ''
      } ${formatHour(starts[i])} - ${formatHour(ends[i])}`;
      i = j + 1;
      if (i < 7) summary += ', ';
    }
    return summary;
  }
  function formatHour(val) {
    const h = parseInt(val, 10);
    if (isNaN(h)) return val;
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour = h % 12 === 0 ? 12 : h % 12;
    return `${hour}:00 ${ampm}`;
  }

  return (
    <div className="relative">
      <button
        type="button"
        className="w-full flex items-center justify-between border rounded-xl px-4 py-3 bg-white text-base shadow-sm hover:shadow-md transition-all"
        onClick={() => setDropdownOpen((v) => !v)}>
        <span className="flex items-center gap-2">
          <Clock className="h-5 w-5 text-muted-foreground" />
          {getSummary()}
        </span>
        <svg
          className={`h-5 w-5 ml-2 transition-transform ${
            dropdownOpen ? 'rotate-180' : ''
          }`}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {dropdownOpen && (
        <div
          ref={ref}
          className="absolute z-50 mt-2 w-full max-w-md bg-white rounded-xl shadow-xl border p-4">
          <div className="font-semibold text-base mb-2">Set Business Hours</div>
          {days.map((day, i) => (
            <DayRow
              key={day}
              dayName={day}
              value={values.businessHours[day.toLowerCase()]}
              setFieldValue={(d, v) =>
                setFieldValue(`businessHours.${d.toLowerCase()}`, v)
              }
              start={starts[i]}
              end={ends[i]}
              setStart={(val) =>
                setStarts((s) => {
                  const arr = [...s];
                  arr[i] = val;
                  return arr;
                })
              }
              setEnd={(val) =>
                setEnds((s) => {
                  const arr = [...s];
                  arr[i] = val;
                  return arr;
                })
              }
              isActive={active[i]}
              setActive={(val) =>
                setActive((a) => {
                  const arr = [...a];
                  arr[i] = typeof val === 'function' ? val(a[i]) : val;
                  return arr;
                })
              }
            />
          ))}
        </div>
      )}
    </div>
  );
};
export default BusinessHours;
